<script>
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';

  import questionnaire from './../libs/questionnaire.json';
  import { api } from './../libs/api.js';
  import { maskQuestionnaire } from './../libs/utils.js';
  import { answers } from './../libs/stores.js';
  import { computeScores, computeResultId, exportAnswerObject } from './../libs/utils.js';
  
  import Footer from "./../components/Footer.svelte";

  export let config;

  let current_page = 0;
  let answer_missing = false;
  let sections = maskQuestionnaire(questionnaire, config);

  function check_answers() {
    let section = sections[current_page];
    let all_answered = section.questions.every(e => e.a != undefined);

    if (all_answered) {
      answer_missing = false;
    } else {
      answer_missing = true;
    }

    return all_answered;
  }

  function handle_next(ev) {
    if (check_answers()) current_page++;

    window.scrollTo(0, 0); // go back to top
  }

  async function handle_finish(ev) {
    if (check_answers()) {
      let scores = computeScores(sections);
      $answers.a = exportAnswerObject(sections);
      await api.post_results($answers);
      navigate("/results/" + computeResultId(config, scores));
    }

    window.scrollTo(0, 0); // go back to top
  }

</script>

<div>

  <!-- navbar -->
  <div class="navbar bg-neutral-content flex-col sm:flex-row">
    <div class="flex-1">
      <h1 class="text-2xl font-bold ml-4">ONAT4All
          <small class="opacity-30"> 
              S<span class="hidden md:inline">elf</span><!--
              --><span class="hidden md:inline">-</span><!--
              -->A<span class="hidden md:inline">assessment</span>
              T<span class="hidden md:inline">ool</span>
          </small>
      </h1>
    </div>
    
    <div class="flex-none mt-2 mb-4 sm:my-0 sm:mx-4">

      <ul class="steps">
        {#each sections as section, index}
          <li class="step" class:step-secondary={index <= current_page}></li>
        {/each}
      </ul>

    </div>
  </div>
  
  <!-- content -->
  <div class="container px-4 sm:px-0 pt-6 pb-16 sm:mx-auto prose">

    {#each sections as section, s_index}
      <div class:hidden={s_index != current_page}>

        <!-- alert -->
        <div class="alert alert-error shadow-lg" class:hidden={!answer_missing}>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span> {$_('questionnaire.answer_required')} </span>
          </div>
        </div>

        <h2 class="text-center"> {$_(section.section)} </h2>
        <p class="italic mb-10"> {$_('questionnaire.description')} </p>

        {#each section.questions as question, q_index}
          <!-- question -->
          <div class="mt-4">
            <h4 class="relative inline">{$_('questionnaire.question')} {q_index + 1}
              <span class="text-success top-0 -left-4 absolute" class:hidden={question.a == undefined}>✓</span>
            </h4>

            {#if question.indicators}
              <div class="float-right h-6">
                {#each question.indicators as indicator}
                  <img class="inline-block w-4 h-full my-0 mx-1" src="/assets/{indicator}.svg" />
                {/each}
              </div>
            {/if}

            <p class="text-justify"> {$_(question.q)} </p>

            {#if Array.isArray(question.o)}
              <div class="ml-4 sm:ml-6">
                {#each question.o as option, o_index}
                  <div class="form-control">
                    <label class="label cursor-pointer justify-start">
                      <input type="radio" name="r-{s_index}-{q_index}" class="radio checked:bg-primary" value={question.s[o_index]} bind:group={question.a} />
                      <span class="label-text ml-4"> {$_(option)} </span>
                    </label>
                  </div>
                {/each}
              </div>
            {:else}
              <div class="flex justify-center">
                  <div class="rating" class:opacity-25={question.a == undefined}>
                    {#each question.s as score}
                      <input type="radio" name="r-{s_index}-{q_index}" class="mask mask-star-2 bg-orange-400" value={score} bind:group={question.a} />
                    {/each}
                  </div>
              </div>
            {/if}
          </div>
        {/each}

        <div class="mt-16">
          <h4>{$_('questionnaire.comment_box')}</h4>
          <p class="text-justify"> {$_('questionnaire.comment_cta')} </p>
          <textarea class="textarea textarea-bordered w-full" bind:value="{section.comments}"></textarea>
        </div>

      </div>
    {/each}
  </div>

  <!-- buttons -->
  <div class="w-5/6 lg:w-1/3 mx-auto mt-4 mb-8 flex justify-between flex-row-reverse">
    <!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com
    License - https://fontawesome.com/license (Commercial License)
    Copyright 2022 Fonticons, Inc. -->
  
    {#if current_page != sections.length - 1}
      <button class="btn btn-secondary gap-2" on:click={handle_next}>
        Next <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 256 512" fill="currentColor"><path d="M64 448c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L178.8 256L41.38 118.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l160 160c12.5 12.5 12.5 32.75 0 45.25l-160 160C80.38 444.9 72.19 448 64 448z"/></svg>
      </button>
    {:else}
      <button class="btn btn-secondary gap-2" on:click={handle_finish}>
        Show Results <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 576 512" fill="currentColor"><path d="M509.5 .0234c-6.145 0-12.53 1.344-18.64 4.227c-44.11 20.86-76.81 27.94-104.1 27.94c-57.89 0-91.53-31.86-158.2-31.87C195 .3203 153.3 8.324 96 32.38V32c0-17.75-14.25-32-32-32S32 14.25 32 32L31.96 496c0 8.75 7.25 16 16 16H80C88.75 512 96 504.8 96 496V384c51.74-23.86 92.71-31.82 128.3-31.82c71.09 0 120.6 31.78 191.7 31.78c30.81 0 65.67-5.969 108.1-23.09C536.3 355.9 544 344.4 544 332.1V30.74C544 12.01 527.8 .0234 509.5 .0234zM480 141.8c-31.99 14.04-57.81 20.59-80 22.49v80.21c25.44-1.477 51.59-6.953 80-17.34V308.9c-22.83 7.441-43.93 11.08-64.03 11.08c-5.447 0-10.71-.4258-15.97-.8906V244.5c-4.436 .2578-8.893 .6523-13.29 .6523c-25.82 0-47.35-4.547-66.71-10.08v66.91c-23.81-6.055-50.17-11.41-80-12.98V213.1C236.2 213.7 232.5 213.3 228.5 213.3C208.8 213.3 185.1 217.7 160 225.1v69.1C139.2 299.4 117.9 305.8 96 314.4V250.7l24.77-10.39C134.8 234.5 147.6 229.9 160 225.1V143.4C140.9 148.5 120.1 155.2 96 165.3V101.8l24.77-10.39C134.8 85.52 147.6 80.97 160 77.02v66.41c26.39-6.953 49.09-10.17 68.48-10.16c4.072 0 7.676 .4453 11.52 .668V65.03C258.6 66.6 274.4 71.55 293.2 77.83C301.7 80.63 310.7 83.45 320 86.12v66.07c20.79 6.84 41.45 12.96 66.71 12.96c4.207 0 8.781-.4766 13.29-.8594V95.54c25.44-1.477 51.59-6.953 80-17.34V141.8zM240 133.9v80.04c18.61 1.57 34.37 6.523 53.23 12.8C301.7 229.6 310.7 232.4 320 235.1V152.2C296.1 144.3 271.6 135.8 240 133.9z"/></svg>
      </button>
    {/if}
  </div>

  <Footer/>

</div>

